import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BookOpenText, SpinnerGap, LinkBreak, WhatsappLogo, ShareNetwork, Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { evento: "Evento", restaurante: "Restaurante", buffet: "Buffet", degustacao: "Degustação", catering: "Catering", outro: "Outro" };

export default function PublicMenu() {
  const { shareToken } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!shareToken) { setError("Link inválido"); setLoading(false); return; }
    axios.get(`${API}/menus/public/${shareToken}`)
      .then(res => setMenu(res.data))
      .catch(() => setError("Cardápio não encontrado ou link expirado"))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const shareUrl = window.location.href;

  const handleWhatsAppShare = () => {
    const items = (menu.sections || []).flatMap(s =>
      (s.items || []).map(i => `  • ${i.sheet_name}${i.price != null ? ` — R$ ${i.price.toFixed(2)}` : ""}`)
    );
    const text = [
      `*${menu.business_name ? menu.business_name + " — " : ""}${menu.name}*`,
      menu.theme ? `_${menu.theme}_` : "",
      "",
      ...items,
      "",
      `Veja o cardápio completo: ${shareUrl}`,
    ].filter(Boolean).join("\n");
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Erro ao copiar"); }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: menu.name, text: `Confira o cardápio: ${menu.name}`, url: shareUrl });
      } catch (e) {
        if (e?.name !== "AbortError") console.debug("Share cancelado pelo usuário:", e?.message);
      }
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <SpinnerGap size={40} className="animate-spin text-stone-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center text-center px-4">
      <LinkBreak size={56} className="text-stone-300 mb-4" />
      <p className="text-stone-600 font-medium text-lg">{error}</p>
      <p className="text-stone-400 text-sm mt-1">O link pode ter sido desativado pelo proprietário</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-stone-900 text-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          {menu.business_name && <p className="text-stone-400 text-xs tracking-[0.3em] uppercase mb-3">{menu.business_name}</p>}
          <h1 className="text-3xl sm:text-4xl font-serif font-light tracking-wide">{menu.name}</h1>
          <div className="flex items-center justify-center gap-3 mt-4 text-xs text-stone-400">
            {menu.theme && <span>{menu.theme}</span>}
            {menu.theme && menu.menu_type && <span className="text-stone-600">·</span>}
            <span>{TYPE_LABELS[menu.menu_type] || menu.menu_type}</span>
            {menu.guest_count && <><span className="text-stone-600">·</span><span>{menu.guest_count} convidados</span></>}
          </div>

          {/* Share buttons */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={handleWhatsAppShare}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
              data-testid="public-menu-whatsapp-btn">
              <WhatsappLogo size={16} weight="fill" /> WhatsApp
            </button>
            <button onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-700 hover:bg-stone-600 text-white text-xs font-medium transition-colors"
              data-testid="public-menu-copy-btn">
              {copied ? <Check size={16} weight="bold" /> : <Copy size={16} />}
              {copied ? "Copiado!" : "Copiar Link"}
            </button>
            {typeof navigator.share === "function" && (
              <button onClick={handleNativeShare}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-700 hover:bg-stone-600 text-white text-xs font-medium transition-colors"
                data-testid="public-menu-share-btn">
                <ShareNetwork size={16} /> Compartilhar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-2xl mx-auto py-10 px-4">
        {(menu.sections || []).map((section, sIdx) => (
          <div key={section.name || sIdx} className="mb-10">
            <h2 className="text-lg font-serif font-light tracking-wide text-stone-800 border-b border-stone-200 pb-2 mb-4 text-center uppercase">{section.name}</h2>
            <div className="space-y-4">
              {(section.items || []).map((item, iIdx) => (
                <div key={item.sheet_id || item.sheet_name || iIdx} className="flex items-start justify-between group">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800">{item.sheet_name}</p>
                    {item.description && <p className="text-sm text-stone-500 mt-0.5 italic">{item.description}</p>}
                  </div>
                  {item.price != null && (
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <span className="border-b border-dotted border-stone-300 w-12 hidden sm:block" />
                      <span className="text-stone-700 font-medium text-sm">R$ {item.price.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {section.notes && <p className="text-xs text-stone-400 mt-3 italic text-center">{section.notes}</p>}
          </div>
        ))}

        {menu.general_notes && (
          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-xs text-stone-400 italic">{menu.general_notes}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-stone-900 text-center py-6">
        <p className="text-stone-500 text-xs">
          <BookOpenText size={14} className="inline-block mr-1 -mt-0.5" />
          Criado com SOP & RAFT
        </p>
      </div>
    </div>
  );
}
